import {
  Maybe,
  MutationSubmitV2APaymentArgs,
  Scalars,
  Typenames,
  V2AExternalRecipient,
  V2AInternalRecipient,
  V2ARecipientsConnection,
  V2ARecipientsEdge,
} from '@varo/graphql-schema-types';
import states from 'features/v2a/receiver/helpers/stateList.json';
import {
  Eggplant,
  Forest,
  Lavender,
  Lime,
  Sunset,
  UserAvatarTheme,
} from 'shared/components/vxl/avatar/UserAvatar/UserAvatar.helpers';
import { emailSchema } from 'shared/components/vxl/input/schemas/email.schema';
import { isNotNilOrEmpty } from 'shared/utils/isNotNilOrEmpty/isNotNilOrEmpty';
import { formatPhoneNumber } from 'shared/utils/string/phone';

export const TRANSFER_LEARN_MORE =
  'https://support.varomoney.com/hc/en-us/articles/18982721784980-Receiving-Varo-to-Anyone-Payments';

interface Email {
  address: Maybe<string>[];
  verified: boolean;
}

export interface AllLocalRecipients {
  __typename?: string;
  id: string;
  displayName: string;
  email?: Email;
  phoneNumber?: Maybe<Scalars['PhoneNumber']>[];
}

export type AllRecipients = V2AExternalRecipient | V2AInternalRecipient;

export type AllRecipientAndLocalRecipient = AllRecipients | AllLocalRecipients;

export interface LimitInfoResponse {
  message: string;
  title: string;
}

export interface SubmitV2AResponse {
  hasError: boolean;
  message: string;
  title?: string;
  educationMessage?: string;
  code?: string;
}

export type V2APaymentData = {
  recipient: AllRecipientAndLocalRecipient;
  amount: string;
  note: string;
};

export type SubmitV2ATransferVariables = {
  token: MutationSubmitV2APaymentArgs['idempotencyId'];
  input: MutationSubmitV2APaymentArgs['input'];
};

export interface V2ARecipients {
  v2aRecipients: V2ARecipientsConnection;
}

export enum V2ASendMoneyWorkflowStep {
  Entry = 'SEND_MONEY_ENTRY',
  Confirm = 'SEND_MONEY_CONFIRM',
}

export interface IV2APaymentDataStates {
  recipient: AllRecipients;
  amount: string;
  note: string;
}

export interface State {
  name: string;
  abbreviation: string;
}

const isV2AInternalRecipient = (
  recipient: AllRecipientAndLocalRecipient,
): recipient is V2AInternalRecipient => {
  return (
    recipient.__typename === Typenames.V2AInternalRecipient &&
    'name' in recipient
  );
};

export const getAvatarInitials = (
  recipient: AllRecipientAndLocalRecipient | string,
): string | undefined => {
  let displayName: string | undefined;

  if (typeof recipient === 'string') {
    displayName = recipient;
  } else if (isV2AInternalRecipient(recipient)) {
    const initials = recipient.name?.initials;
    if (initials) {
      return initials;
    }
    displayName = recipient.displayName;
  } else {
    displayName = recipient?.displayName;
  }

  if (!displayName?.trim()) {
    return undefined;
  }

  if (displayName.length <= 2) {
    return displayName[0].toUpperCase();
  }

  if (getIsEmail(displayName)) {
    return displayName[0].toUpperCase();
  }

  return displayName.match(/\b\w/g)?.slice(0, 2).join('').toUpperCase() || '';
};

export const displayInfo = (
  recipient: AllRecipientAndLocalRecipient,
): string => {
  const phoneNumber = Array.isArray(recipient?.phoneNumber)
    ? recipient.phoneNumber[0]
    : recipient?.phoneNumber;

  const email = Array.isArray(recipient?.email?.address)
    ? recipient.email.address[0]
    : recipient?.email?.address;

  return phoneNumber ? formatPhoneNumber(phoneNumber, false) : email || '';
};

export const verifyIsPhone = (value?: string | null): boolean => {
  return /^(\+1)?\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/.test(value || '');
};

export const getRecipientList = (
  v2aRecipients?: V2ARecipientsConnection,
): Array<AllRecipients> => {
  const recipientList: Array<AllRecipients> =
    v2aRecipients?.edges?.map((edge: V2ARecipientsEdge) => edge.node) || [];
  return recipientList;
};

export const defaultV2AProperties = {
  flow_name: 'transfer_money_v2a_sender',
};

export const getIsEmail = (recipient?: AllRecipients | string | null) => {
  if (!recipient) {
    return false;
  } else if (typeof recipient === 'string') {
    return emailSchema.safeParse(recipient).success;
  }
  return (
    !!recipient?.id &&
    !!recipient?.email?.address &&
    !isV2AInternalRecipient(recipient)
  );
};

export const getIsPhone = (
  recipient?: AllRecipientAndLocalRecipient | string | null,
) => {
  if (!recipient) {
    return false;
  } else if (typeof recipient === 'string') {
    return verifyIsPhone(recipient);
  }

  const phoneNumber = Array.isArray(recipient.phoneNumber)
    ? recipient.phoneNumber[0]
    : recipient.phoneNumber;

  return (
    Boolean(recipient.id) && Boolean(phoneNumber) && verifyIsPhone(phoneNumber)
  );
};

export const shouldUseInitialsForAvatar = (
  recipient?: AllRecipients | AllLocalRecipients | string | null,
): boolean => typeof recipient === 'string' && /^[a-zA-Z]/.test(recipient);

export const EXTERNAL_USER = 'externalUser';
export const NOT_FOUND =
  'The user could not be found using the contact info you entered. Please try a different email address or phone number.';

export const generateAvatarTheme = (name: string): UserAvatarTheme => {
  //Color is based on last char in name string
  if (!name.length) {
    return Forest;
  }

  return getAvatarTheme(name);
};

export const getRecipientContactName = (
  recipient?: AllRecipientAndLocalRecipient,
) => {
  if (!recipient || recipient?.id === EXTERNAL_USER) {
    return '';
  }

  return recipient.displayName;
};

export const getAvatarTheme = (name: string | undefined) => {
  //Forest is catch all for non a-z characters as well as b,g,l,q,v and undefined
  if (!name) {
    return Forest;
  }
  const colorBasedInitial = name[name.length - 1].toLowerCase();
  switch (colorBasedInitial) {
    case 'a':
    case 'f':
    case 'k':
    case 'p':
    case 'u':
    case 'z':
      return Lavender;
    case 'c':
    case 'h':
    case 'm':
    case 'r':
    case 'w':
      return Sunset;
    case 'd':
    case 'i':
    case 'n':
    case 's':
    case 'x':
      return Lime;
    case 'e':
    case 'j':
    case 'o':
    case 't':
    case 'y':
      return Eggplant;
    default:
      return Forest;
  }
};

export const getPrimaryContact = (
  selectedRecipient?: AllRecipientAndLocalRecipient,
) => {
  if (Array.isArray(selectedRecipient?.email?.address)) {
    return selectedRecipient.email.address[0];
  }

  if (selectedRecipient?.email?.address) {
    return selectedRecipient.email.address;
  }

  if (Array.isArray(selectedRecipient?.phoneNumber)) {
    return selectedRecipient.phoneNumber[0];
  }

  return selectedRecipient?.phoneNumber || '';
};

export const getSubmitVariables = (
  paymentData: V2APaymentData,
  displayName: string | null,
  token: string,
): SubmitV2ATransferVariables => {
  const recipient = paymentData.recipient;
  const emailAddress = Array.isArray(recipient.email?.address)
    ? recipient.email.address[0]
    : recipient.email?.address;
  const phoneNumber = Array.isArray(recipient.phoneNumber)
    ? recipient.phoneNumber[0]
    : recipient.phoneNumber;
  const nickname = displayName ?? recipient.displayName ?? null;

  if (isV2AInternalRecipient(recipient)) {
    return {
      token,
      input: {
        amount: {
          amount: paymentData.amount,
          currency: 'USD',
        },
        note: paymentData.note,
        internalUserInput: {
          recipientId: recipient.id,
          nickname,
        },
      },
    };
  }

  return {
    token,
    input: {
      amount: {
        amount: paymentData.amount,
        currency: 'USD',
      },
      note: paymentData.note,
      externalUserInput: {
        recipientId: recipient.id === EXTERNAL_USER ? null : recipient.id,
        ...(emailAddress && { email: emailAddress }),
        ...(phoneNumber && { phone: phoneNumber }),
        nickname,
      },
    },
  };
};

export const handleSpecialCharacters = (valueToCheck: string) => {
  return valueToCheck.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export const getFilteredRecipients = (
  recipientListToFilter: AllRecipientAndLocalRecipient[],
  valueToFilter: string,
): AllRecipientAndLocalRecipient[] => {
  const regex = new RegExp(handleSpecialCharacters(valueToFilter), 'ig');
  return recipientListToFilter.filter(
    (recipient) =>
      (recipient.phoneNumber && regex.test(recipient.phoneNumber as string)) ||
      (recipient?.email?.address &&
        regex.test(recipient.email.address as string)) ||
      (recipient?.displayName && regex.test(recipient.displayName)) ||
      regex.test((recipient as V2AExternalRecipient)?.nickName ?? ''),
  );
};

export const recipientAddresses = (item: AllRecipientAndLocalRecipient) => {
  const phoneNumbers = Array.isArray(item?.phoneNumber ?? [])
    ? new Set(item?.phoneNumber ?? [])
    : [];
  const emailAddresses = Array.isArray(item?.email?.address ?? [])
    ? new Set(item?.email?.address ?? [])
    : [];
  return [...phoneNumbers, ...emailAddresses].filter(isNotNilOrEmpty);
};

export const hasMultipleContacts = (
  item: AllRecipientAndLocalRecipient,
): boolean => {
  return recipientAddresses(item).length > 1;
};

export const getContactType = (
  selectedRecipient?: AllRecipientAndLocalRecipient,
): string => {
  if (selectedRecipient?.email) {
    return 'email';
  } else if (selectedRecipient?.phoneNumber) {
    return 'phone';
  }
  return '';
};

export const getReceiverType = (
  selectedRecipient?: AllRecipientAndLocalRecipient,
): string => {
  if (selectedRecipient?.__typename === Typenames.V2AExternalRecipient) {
    return 'external';
  }
  return 'varo_customer';
};

export const stateList: Array<State> = states;
